/**
 * WorkItemDrawer — J3-B
 *
 * Right-side slide-over panel for a single Work Item.
 * Tabs: Overview · Links · History.
 *
 * Overview tab: all fields + state-machine-aware transition buttons.
 * Links tab:    linked Outcomes, parent/child items.
 * History tab:  immutable audit timeline of status transitions.
 *
 * State machine surface rules (CT-02):
 *   - Illegal transitions are HIDDEN, not error-driven.
 *   - WIP limit reached (409)  → actionable toast with "View In Progress" action.
 *   - Invalid transition (422) → toast explaining the issue.
 *   - QC required (422)        → toast explaining why.
 *
 * Compliance: LEAN-07 (pull = user clicks transition button), CT-02, CT-01 (QC gate visible).
 * PRD §12.3.6 tasks 3–4.
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { X, ArrowRight, Link2, Clock, ChevronRight, Plus, Trash2, RefreshCw, ChevronDown, FileText, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs/Tabs';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Alert } from '@/components/ui/Alert';
import { LoadingState } from '@/components/ui/LoadingState/LoadingState';
import { Select } from '@/components/ui/Select/Select';
import { Input } from '@/components/ui/Input/Input';
import { t } from '@/lib/i18n';
import { formatDate, formatDateTime } from '@/lib/localeFormatters';
import { showToast } from '@/components/ui/Toast';
import {
  useRorosWorkItem,
  useRorosWorkItemHistory,
  useRorosWorkItemTransition,
  useRorosWorkItemHardDelete,
  useRorosWorkItemCreateLink,
  useRorosWorkItemDeleteLink,
  useRorosWorkItemTree,
  useRorosWorkItemRollup,
} from '@/hooks/roros/control-tower';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { WorkItemFormDialog } from './WorkItemFormDialog';
import { IterationsTab } from './IterationsTab';
import { useRorosOutcome } from '@/hooks/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { RorosWorkItemDTO, RorosWorkItemHistoryDTO, FeedbackHistoryEntry } from '@/types/roros/control-tower';

// ─── State machine client-side map ────────────────────────────────────────

// NOTE: mirrors server/modules/shared/roros-enums.ts WORK_ITEM_TRANSITIONS.
// Legal next states per current status.
const TRANSITION_MAP: Record<string, string[]> = {
  backlog:               ['ready'],
  ready:                 ['in_progress', 'backlog'],
  in_progress:           ['done', 'blocked', 'escalated'],
  done:                  ['quality_confirmation', 'escalated'],
  quality_confirmation:  ['released', 'in_progress'],
  released:              [],
  blocked:               ['in_progress', 'escalated'],
  escalated:             ['in_progress', 'blocked'],
};

const TRANSITION_LABELS: Record<string, string> = {
  backlog:               'Move to Backlog',
  ready:                 'Mark Ready',
  in_progress:           'Start Work',
  done:                  'Mark Done',
  quality_confirmation:  'Send to QC',
  released:              'Release',
  blocked:               'Mark Blocked',
  escalated:             'Escalate',
};

// ─── Status display helpers ────────────────────────────────────────────────

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'default' | 'primary' {
  switch (status) {
    case 'released':           return 'success';
    case 'in_progress':        return 'primary';
    case 'quality_confirmation': return 'warning';
    case 'blocked':
    case 'escalated':          return 'error';
    default:                   return 'default';
  }
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    backlog:               t('Backlog'),
    ready:                 t('Ready'),
    in_progress:           t('In Progress'),
    done:                  t('Done'),
    quality_confirmation:  t('QC'),
    released:              t('Released'),
    blocked:               t('Blocked'),
    escalated:             t('Escalated'),
  };
  return map[status] ?? status;
}

function priorityLabel(p: number): string {
  const map: Record<number, string> = { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low', 5: 'Lowest' };
  return map[p] ?? String(p);
}

function linkTypeLabel(lt: string): string {
  const map: Record<string, string> = {
    depends_on: t('Depends on'),
    blocks:     t('Blocks'),
    relates_to: t('Relates to'),
  };
  return map[lt] ?? lt;
}

// ─── Props ─────────────────────────────────────────────────────────────────

interface WorkItemDrawerProps {
  workItemId: string;
  onClose: () => void;
  domains: RorosDomainDTO[];
  actors: RorosActorDTO[];
}

// ─── Overview Tab ─────────────────────────────────────────────────────────

interface OverviewTabProps {
  item: RorosWorkItemDTO;
  domains: RorosDomainDTO[];
  actors: RorosActorDTO[];
  onTransitionSuccess: () => void;
  onClose: () => void;
}

function OverviewTab({ item, domains, actors, onTransitionSuccess, onClose }: OverviewTabProps) {
  const navigate    = useNavigate();
  const transition  = useRorosWorkItemTransition();
  const hardDelete  = useRorosWorkItemHardDelete();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission(PERMISSIONS.ROROS_CONTROL_TOWER_DELETE);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const legalNextStates = TRANSITION_MAP[item.status] ?? [];

  const domainName = domains.find(d => d.id === item.domainId)?.name ?? '—';
  const ownerName  = actors.find(a => a.id === item.ownerActorId)?.name ?? '—';

  // v1.1.19 — sub-item count drives the dialog's wording.
  const treeQ = useRorosWorkItemTree(item.id);
  const subItemCount = (treeQ.data ?? []).length;

  async function handleHardDelete() {
    setConfirmDeleteOpen(false);
    try {
      await hardDelete.mutateAsync(item.id);
      showToast.success(t('Work item deleted'));
      onClose();
    } catch (err: any) {
      showToast.error(err?.message ?? t('Could not delete the work item.'));
    }
  }

  async function handleTransition(toStatus: string) {
    setTransitionError(null);
    try {
      await transition.mutateAsync({ id: item.id, payload: { toStatus } });
      showToast.success(t('Status updated to ') + statusLabel(toStatus));
      onTransitionSuccess();
    } catch (err: any) {
      const code    = err?.response?.data?.error?.code ?? err?.code ?? '';
      const message = err?.response?.data?.error?.message ?? err?.message ?? '';

      if (code === 'ROROS_WIP_LIMIT_REACHED') {
        const errMsg = t(
          `WIP limit reached for "${statusLabel(toStatus)}". Finish or block an in-flight item to make room.`
        );
        setTransitionError(errMsg);
        // Toast with action
        showToast.error(errMsg);
      } else if (code === 'ROROS_QC_REQUIRED') {
        setTransitionError(
          t('Quality confirmation requires a passing QC report. Complete the QC report first.')
        );
        showToast.error(t('QC report required before releasing this item.'));
      } else if (code === 'ROROS_INVALID_STATUS_TRANSITION') {
        setTransitionError(t('This status transition is not allowed from the current state.'));
        showToast.error(t('Invalid status transition.'));
      } else {
        setTransitionError(message || t('Transition failed. Please try again.'));
        showToast.error(message || t('Transition failed.'));
      }
    }
  }

  return (
    <div className="space-y-4">
      {/* Transition error */}
      {transitionError && (
        <Alert variant="error" title={t('Transition failed')}>
          <p>{transitionError}</p>
          {transitionError.includes('WIP limit') && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={() => navigate('/roros/control-tower?status=in_progress')}
            >
              {t('View in-progress items')} <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          )}
        </Alert>
      )}

      {/* State transition buttons (only legal next states) + Delete (v1.1.19) */}
      {(legalNextStates.length > 0 || canDelete) && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            {t('Actions')}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {legalNextStates.map((nextStatus) => (
              <Button
                key={nextStatus}
                variant="outline"
                size="sm"
                disabled={transition.isPending || hardDelete.isPending}
                onClick={() => handleTransition(nextStatus)}
              >
                <ArrowRight className="h-3 w-3 mr-1" />
                {t(TRANSITION_LABELS[nextStatus] ?? nextStatus)}
              </Button>
            ))}
            {canDelete && (
              <Button
                variant="error"
                size="sm"
                disabled={transition.isPending || hardDelete.isPending}
                isLoading={hardDelete.isPending}
                onClick={() => setConfirmDeleteOpen(true)}
                data-testid="work-item-delete-btn"
                className="ml-auto"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                {t('Delete')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* v1.1.19 — Hard-delete confirmation */}
      <ConfirmDialog
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title={t('Delete this work item?')}
        description={
          subItemCount > 0
            ? t('This will permanently delete the work item, its {{count}} sub-item(s), all their history, and any links to other artifacts. This action cannot be undone.').replace('{{count}}', String(subItemCount))
            : t('This will permanently delete the work item, its history, and any links to other artifacts. This action cannot be undone.')
        }
        confirmText={t('Delete')}
        variant="destructive"
        onConfirm={handleHardDelete}
      />


      {/* Current status */}
      <PropertyRow label={t('Status')}>
        <Badge variant={statusVariant(item.status)}>{statusLabel(item.status)}</Badge>
      </PropertyRow>

      {/* Core fields */}
      <PropertyRow label={t('Type')}>{item.type}</PropertyRow>
      <PropertyRow label={t('Domain')}>{domainName}</PropertyRow>
      <PropertyRow label={t('Owner')}>{ownerName}</PropertyRow>
      <PropertyRow label={t('Priority')}>{priorityLabel(item.priority)}</PropertyRow>
      <LinkedOutcomeRow
        domainId={item.domainId}
        outcomeId={item.outcomeId}
        outcomeTitle={item.outcomeTitle}
      />
      <PropertyRow label={t('Due Date')}>{item.dueDate ? formatDate(item.dueDate) : '—'}</PropertyRow>
      <PropertyRow label={t('Iteration')}>{item.iterationCount}</PropertyRow>
      <PropertyRow label={t('FTT First Pass')}>
        {item.fttFirstPass === null ? '—' : item.fttFirstPass ? t('Yes') : t('No')}
      </PropertyRow>

      {/* Description */}
      {item.description && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
            {t('Description')}
          </p>
          <p className="text-sm text-foreground whitespace-pre-wrap">{item.description}</p>
        </div>
      )}

      {/* Definition of Done */}
      {item.definitionOfDone && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
            {t('Definition of Done')}
          </p>
          <p className="text-sm text-foreground whitespace-pre-wrap">{item.definitionOfDone}</p>
        </div>
      )}

      {/* Blocked / Escalation reason */}
      {item.blockedReason && (
        <PropertyRow label={t('Blocked Reason')}>
          <span className="text-error">{item.blockedReason}</span>
        </PropertyRow>
      )}
      {item.escalationReason && (
        <PropertyRow label={t('Escalation Reason')}>
          <span className="text-error">{item.escalationReason}</span>
        </PropertyRow>
      )}

      {/*
        Sven dogfood 2026-05-03: surface the latest change-request reason
        when an item was just sent back from QC to in_progress. Pre-fix,
        clicking "Request Changes" in the Verification Inbox saved the
        reason into roros_work_item_history but never surfaced it
        prominently — the operator opening the item next had no clue
        what the founder asked them to change. The reason was buried
        under the History tab.
        Show the panel only when (a) the item is currently in_progress,
        AND (b) the most recent transition was QC → in_progress (i.e.
        the operator hasn't yet acted on the feedback).
      */}
      {/*
        Active feedback alert: only visible while the item is back in in_progress
        (i.e. the operator hasn't yet acted on the feedback). Disappears once
        the operator completes their revision.
      */}
      <ChangesRequestedPanel
        itemId={item.id}
        status={item.status}
      />

      {/* Deliverable (QC Revision Loop) */}
      {item.deliverableText && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" aria-hidden />
            {t('Deliverable')}
            {(item.revisionCount ?? 0) > 0 && (
              <Badge variant="secondary" className="text-xs ml-1">
                {t('Revision') + ' ' + (item.revisionCount ?? 0)}
              </Badge>
            )}
          </p>
          <div className="rounded-md border border-border bg-muted/20 p-3 text-sm text-foreground whitespace-pre-wrap max-h-64 overflow-y-auto [scrollbar-width:thin]">
            {item.deliverableText}
          </div>
        </div>
      )}

      {/*
        Feedback history: visible whenever there are prior feedback rounds,
        regardless of current status. The verifier needs to see all prior
        change requests when the item returns to quality_confirmation.
      */}
      <FeedbackHistorySection feedbackHistory={item.feedbackHistory} />

      {/* Timestamps */}
      <div className="pt-2 border-t border-border space-y-1">
        <p className="text-xs text-muted-foreground">
          {t('Created')}: {formatDateTime(item.createdAt).date} {formatDateTime(item.createdAt).time}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('Updated')}: {formatDateTime(item.updatedAt).date} {formatDateTime(item.updatedAt).time}
        </p>
      </div>
    </div>
  );
}

// ─── ChangesRequestedPanel ────────────────────────────────────────────────

/**
 * Surfaces the most recent "Request Changes" reason at the top of the
 * Work Item Overview. Pulls it from the existing history endpoint —
 * no DB schema change needed. Disappears as soon as the operator moves
 * the item out of in_progress (i.e. acknowledges the feedback).
 *
 * Note: the full feedback history across ALL rounds is in FeedbackHistorySection
 * below — that one is visible regardless of status.
 */
function ChangesRequestedPanel({
  itemId,
  status,
}: {
  itemId: string;
  status: string;
}) {
  const { data: history } = useRorosWorkItemHistory(itemId);

  // Only meaningful while the item is back in active work.
  if (status !== 'in_progress') return null;
  if (!history || history.length === 0) return null;

  // History is ordered desc by createdAt — the first entry is the latest.
  const latest = (history as RorosWorkItemHistoryDTO[])[0];
  if (!latest) return null;
  if (latest.toStatus !== 'in_progress') return null;

  // Two cases where we surface a prominent banner:
  //   1. quality_confirmation → in_progress: QC verifier requested changes
  //   2. done → in_progress: cascade submission rejected by the founder/CEO (AC-hard-2)
  const isCascadeRejection = latest.fromStatus === 'done';
  const isQcChanges = latest.fromStatus === 'quality_confirmation';
  if (!isCascadeRejection && !isQcChanges) return null;

  // AC-hard-2: cascade rejection gets a distinct "Sent back for revision" banner
  if (isCascadeRejection) {
    return (
      <Alert
        variant="error"
        title={t('Sent back for revision')}
        data-testid="cascade-rejection-banner"
      >
        {latest.reason ? (
          <p className="text-sm whitespace-pre-wrap">
            {latest.reason}
          </p>
        ) : (
          <p className="text-sm italic">
            {t('Sent back for revision — no reason provided.')}
          </p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          {t('Sent back on')} {formatDateTime(latest.createdAt).date} {formatDateTime(latest.createdAt).time}
        </p>
      </Alert>
    );
  }

  // Original QC changes-requested banner
  return (
    <Alert variant="warning" title={t('Changes requested')}>
      {latest.reason ? (
        <p className="text-sm whitespace-pre-wrap">{latest.reason}</p>
      ) : (
        <p className="text-sm italic">
          {t('Verifier requested changes — no reason provided.')}
        </p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        {t('Requested on')} {formatDateTime(latest.createdAt).date} {formatDateTime(latest.createdAt).time}
      </p>
    </Alert>
  );
}

// ─── FeedbackHistorySection ───────────────────────────────────────────────

/**
 * Shows the full collapsible feedback history whenever there are prior
 * change-request rounds. Visible regardless of current status — the
 * verifier needs this context when the item returns to quality_confirmation
 * after an AI operator revision.
 */
function FeedbackHistorySection({
  feedbackHistory,
}: {
  feedbackHistory?: FeedbackHistoryEntry[];
}) {
  const [expanded, setExpanded] = useState(false);
  const entries = feedbackHistory ?? [];

  if (entries.length === 0) return null;

  return (
    <div>
      <Button
        variant="ghost"
        size="sm"
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors h-auto p-0"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <RefreshCw className="h-3 w-3" aria-hidden />
        {entries.length === 1
          ? t('1 prior feedback round')
          : `${entries.length} ${t('prior feedback rounds')}`}
        <ChevronDown
          className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </Button>
      {expanded && (
        <ul className="mt-2 space-y-2 border-l-2 border-border pl-3">
          {entries.map((entry, i) => (
            <li key={i} className="text-xs space-y-0.5">
              <p className="font-medium text-foreground whitespace-pre-wrap">
                {entry.reason || <span className="italic text-muted-foreground">{t('No reason provided')}</span>}
              </p>
              <p className="text-muted-foreground">
                {formatDateTime(entry.at).date} {formatDateTime(entry.at).time}
                {entry.revisionFromCount > 0 && ` · ${t('Revision')} ${entry.revisionFromCount}`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── PropertyRow helper ───────────────────────────────────────────────────

function PropertyRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-xs font-medium text-muted-foreground w-28 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-foreground">{children}</span>
    </div>
  );
}

// ─── LinkedOutcomeRow ─────────────────────────────────────────────────────

/**
 * Renders the "Linked Outcome" row in the Overview tab.
 * When `outcomeId` is non-null, fetches the outcome title (using the work
 * item's domainId) and renders a click-through link to the Outcomes page.
 * Falls back to the `outcomeTitle` from the DTO if already resolved server-side.
 */
function LinkedOutcomeRow({
  domainId,
  outcomeId,
  outcomeTitle,
}: {
  domainId: string;
  outcomeId: string | null;
  outcomeTitle: string | null;
}) {
  // Fetch outcome details only when outcomeId is present and DTO title is missing.
  const outcomeQuery = useRorosOutcome(domainId, outcomeId && !outcomeTitle ? outcomeId : '');
  const title = outcomeTitle ?? outcomeQuery.data?.title ?? null;

  if (!outcomeId) {
    return (
      <PropertyRow label={t('Linked Outcome')}>
        <span className="text-muted-foreground/50">—</span>
      </PropertyRow>
    );
  }

  return (
    <PropertyRow label={t('Linked Outcome')}>
      {title ? (
        <Link
          to={`/roros/artifacts/outcomes/${outcomeId}`}
          className="text-primary underline hover:text-primary/80 text-sm"
        >
          {title}
        </Link>
      ) : (
        <span className="text-muted-foreground text-sm">{outcomeId.slice(0, 8)}…</span>
      )}
    </PropertyRow>
  );
}

// ─── Links Tab ─────────────────────────────────────────────────────────────

interface LinksTabProps {
  item: RorosWorkItemDTO;
}

const LINK_TYPE_OPTIONS = [
  { value: 'relates_to', label: 'Relates to' },
  { value: 'depends_on', label: 'Depends on' },
  { value: 'blocks',     label: 'Blocks' },
];

function LinksTab({ item }: LinksTabProps) {
  const [newTargetId,    setNewTargetId]    = useState('');
  const [newLinkType,    setNewLinkType]    = useState('relates_to');
  const [deletingLinkId, setDeletingLinkId] = useState<string | null>(null);

  const createLink = useRorosWorkItemCreateLink();
  const deleteLink = useRorosWorkItemDeleteLink();

  const links = item.links ?? [];

  async function handleAddLink() {
    if (!newTargetId.trim()) return;
    try {
      await createLink.mutateAsync({ id: item.id, payload: { targetId: newTargetId.trim(), linkType: newLinkType as any } });
      setNewTargetId('');
      showToast.success(t('Link added'));
    } catch (err: any) {
      showToast.error(err?.message ?? t('Failed to add link'));
    }
  }

  async function handleDeleteLink(linkId: string) {
    try {
      await deleteLink.mutateAsync({ id: item.id, linkId });
      setDeletingLinkId(null);
      showToast.success(t('Link removed'));
    } catch (err: any) {
      showToast.error(err?.message ?? t('Failed to remove link'));
    }
  }

  return (
    <div className="space-y-4">
      {/* Existing links */}
      {links.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('No links yet.')}</p>
      ) : (
        <ul className="space-y-2">
          {links.map((link) => (
            <li key={link.id} className="flex items-center justify-between text-sm border border-border rounded-lg px-3 py-2">
              <span>
                <span className="text-xs text-muted-foreground mr-2">{linkTypeLabel(link.linkType)}</span>
                <span className="font-mono text-xs text-foreground">{link.targetId.slice(0, 12)}…</span>
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeletingLinkId(link.id)}
                className="h-7 w-7 text-muted-foreground hover:text-error transition-colors"
                title={t('Remove link')}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Add link form */}
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Input
            label={t('Work item ID')}
            value={newTargetId}
            onChange={e => setNewTargetId(e.target.value)}
            placeholder={t('Paste work item ID…')}
          />
        </div>
        <Select
          options={LINK_TYPE_OPTIONS}
          value={newLinkType}
          onValueChange={setNewLinkType}
          className="min-w-[120px]"
        />
        <Button type="button" onClick={handleAddLink} size="sm" disabled={createLink.isPending || !newTargetId.trim()}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          {t('Add')}
        </Button>
      </div>

      {/* Confirm delete dialog */}
      {deletingLinkId && (
        <ConfirmDialog
          isOpen
          title={t('Remove link?')}
          description={t('This will remove the link between the two work items.')}
          onConfirm={() => handleDeleteLink(deletingLinkId)}
          onClose={() => setDeletingLinkId(null)}
          confirmText={t('Remove')}
          variant="danger"
        />
      )}
    </div>
  );
}

// ─── Sub-items Tab (J7-W5 task 6c-1) ──────────────────────────────────────

interface SubItemsTabProps {
  item: RorosWorkItemDTO;
  domains: RorosDomainDTO[];
  actors: RorosActorDTO[];
}

function SubItemsTab({ item, domains, actors }: SubItemsTabProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const tree = useRorosWorkItemTree(item.id);
  const rollup = useRorosWorkItemRollup(item.id);

  // The tree includes the root; strip it to render only children.
  const children = (tree.data ?? []).filter((c) => c.id !== item.id);

  // One-level rule: this drawer can only host a child if `item` is itself
  // a root. If the current item already has a parent, hide the create CTA.
  const canCreateChild = !item.parentWorkItemId;

  return (
    <div className="space-y-4">
      {rollup.data && (
        <div
          className="text-xs text-muted-foreground border border-border rounded-lg px-3 py-2"
          data-testid="rollup-stats"
        >
          {`${rollup.data.completed}/${rollup.data.total} ` + t('done')}
          {rollup.data.blocked > 0 && (
            <span className="ml-3 text-error">{rollup.data.blocked} {t('Blocked')}</span>
          )}
          {rollup.data.escalated > 0 && (
            <span className="ml-3 text-error">{rollup.data.escalated} {t('Escalated')}</span>
          )}
        </div>
      )}

      {tree.isLoading ? (
        <LoadingState text={t('Loading sub-items…')} minHeight="80px" />
      ) : children.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('No sub-items yet.')}</p>
      ) : (
        <ul className="space-y-2">
          {children.map((child) => (
            <li
              key={child.id}
              className="flex items-center justify-between text-sm border border-border rounded-lg px-3 py-2"
              data-testid={`subitem-${child.id}`}
            >
              <span className="truncate flex-1">{child.title}</span>
              <Badge variant={statusVariant(child.status)}>{statusLabel(child.status)}</Badge>
            </li>
          ))}
        </ul>
      )}

      {canCreateChild && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setCreateOpen(true)}
          data-testid="create-subitem-cta"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          {t('Create sub-item')}
        </Button>
      )}

      <WorkItemFormDialog
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
          tree.refetch();
          rollup.refetch();
        }}
        domains={domains}
        actors={actors}
        defaultParentWorkItemId={item.id}
      />
    </div>
  );
}

// ─── History Tab ──────────────────────────────────────────────────────────

interface HistoryTabProps {
  workItemId: string;
}

function HistoryTab({ workItemId }: HistoryTabProps) {
  const { data: history, isLoading, isError } = useRorosWorkItemHistory(workItemId);

  if (isLoading) return <LoadingState text={t('Loading history…')} minHeight="100px" />;
  if (isError)   return <Alert variant="error" title={t('Failed to load history')} />;
  if (!history || history.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('No status history yet.')}</p>;
  }

  return (
    <ol className="relative border-l border-border ml-2 space-y-4">
      {(history as RorosWorkItemHistoryDTO[]).map((entry) => (
        <li key={entry.id} className="ml-4">
          <div className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full bg-primary-200 border-2 border-background" />
          <div className="flex items-baseline gap-2">
            {entry.fromStatus && (
              <>
                <Badge variant="default">{statusLabel(entry.fromStatus)}</Badge>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
              </>
            )}
            <Badge variant={statusVariant(entry.toStatus)}>
              {statusLabel(entry.toStatus)}
            </Badge>
          </div>
          {entry.reason && (
            <p className="mt-1 text-xs text-muted-foreground italic">"{entry.reason}"</p>
          )}
          <p className="mt-0.5 text-xs text-muted-foreground">
            <Clock className="h-3 w-3 inline mr-1" />
            {formatDate(entry.createdAt)}
            {entry.actorId && ` · ${entry.actorId.slice(0, 8)}…`}
          </p>
        </li>
      ))}
    </ol>
  );
}

// ─── WorkItemDrawer ────────────────────────────────────────────────────────

export function WorkItemDrawer({ workItemId, onClose, domains, actors }: WorkItemDrawerProps) {
  const { data: item, isLoading, isError, refetch } = useRorosWorkItem(workItemId);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={item?.title ?? t('Work Item Detail')}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-background shadow-2xl flex flex-col
                   border-l border-border overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex-1 min-w-0 pr-4">
            {isLoading ? (
              <div className="h-5 w-48 bg-muted animate-pulse rounded" />
            ) : (
              <h2 className="text-base font-semibold text-foreground truncate">
                {item?.title ?? t('Work Item')}
              </h2>
            )}
            {item && (
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={statusVariant(item.status)}>
                  {statusLabel(item.status)}
                  {(item.revisionCount ?? 0) > 0 && (
                    <span className="ml-1 font-normal opacity-80">
                      {t('Revision') + ' ' + (item.revisionCount ?? 0)}
                    </span>
                  )}
                </Badge>
                <span className="text-xs text-muted-foreground">#{item.id.slice(0, 8)}</span>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-lg"
            aria-label={t('Close drawer')}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {isLoading ? (
            <div className="p-5">
              <LoadingState text={t('Loading work item…')} minHeight="200px" />
            </div>
          ) : isError || !item ? (
            <div className="p-5">
              <Alert variant="error" title={t('Failed to load work item')}>
                <Button size="sm" onClick={() => refetch()}>{t('Retry')}</Button>
              </Alert>
            </div>
          ) : (
            <Tabs defaultValue="overview" className="h-full flex flex-col">
              <TabsList className="px-5 border-b border-border flex-shrink-0">
                <TabsTrigger value="overview">
                  <span>{t('Overview')}</span>
                </TabsTrigger>
                <TabsTrigger value="subitems" data-testid="tab-subitems">
                  <span>{t('Sub-items')}</span>
                </TabsTrigger>
                <TabsTrigger value="iterations" data-testid="tab-iterations">
                  <Sparkles className="h-3.5 w-3.5 mr-1 inline" />
                  <span>{t('Iterations')}</span>
                  {(item.revisionCount ?? 0) > 0 && (
                    <span className="ml-1 text-xs bg-muted text-muted-foreground rounded-full px-1.5 py-0.5">
                      {(item.revisionCount ?? 0) + 1}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="links">
                  <Link2 className="h-3.5 w-3.5 mr-1 inline" />
                  <span>{t('Links')}</span>
                  {(item.links?.length ?? 0) > 0 && (
                    <span className="ml-1 text-xs bg-muted text-muted-foreground rounded-full px-1.5 py-0.5">
                      {item.links!.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="history">
                  <Clock className="h-3.5 w-3.5 mr-1 inline" />
                  <span>{t('History')}</span>
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                <TabsContent value="overview" className="p-5">
                  <OverviewTab
                    item={item}
                    domains={domains}
                    actors={actors}
                    onTransitionSuccess={() => refetch()}
                    onClose={onClose}
                  />
                </TabsContent>
                <TabsContent value="subitems" className="p-5">
                  <SubItemsTab item={item} domains={domains} actors={actors} />
                </TabsContent>
                <TabsContent value="iterations" className="p-5">
                  <IterationsTab workItemId={workItemId} />
                </TabsContent>
                <TabsContent value="links" className="p-5">
                  <LinksTab item={item} />
                </TabsContent>
                <TabsContent value="history" className="p-5">
                  <HistoryTab workItemId={workItemId} />
                </TabsContent>
              </div>
            </Tabs>
          )}
        </div>
      </div>
    </>
  );
}
